import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardSummaryApi } from "../../../api/api";
import "./Dashboard.scss";

// ─── Small reusable SVGs ──────────────────────────────────────────────────────
const ArrowUp = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);
const ArrowDown = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const ArrowRight = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const RefreshIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

// ─── Skeleton Loader ──────────────────────────────────────────────────────────
const CardLoader = ({ variant = "default" }) => {
  if (variant === "kpi") {
    return (
      <div className="db-skeleton-kpi">
        <div className="db-skeleton db-skeleton--value" />
        <div className="db-skeleton db-skeleton--label" />
        <div className="db-skeleton db-skeleton--action" />
      </div>
    );
  }

  if (variant === "activity") {
    return (
      <div className="db-skeleton-list">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="db-skeleton-activity-item"
            style={{ animationDelay: `${i * 0.05}s` }}
          >
            <div className="db-skeleton db-skeleton--avatar" />
            <div className="db-skeleton-activity-lines">
              <div className="db-skeleton db-skeleton--line-long" />
              <div className="db-skeleton db-skeleton--line-short" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (variant === "table") {
    return (
      <div className="db-skeleton-table">
        <div className="db-skeleton-table-header">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="db-skeleton db-skeleton--th" />
          ))}
        </div>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="db-skeleton-table-row"
            style={{ animationDelay: `${i * 0.04}s` }}
          >
            <div className="db-skeleton-table-cell db-skeleton-table-cell--wide">
              <div className="db-skeleton db-skeleton--cell-title" />
              <div className="db-skeleton db-skeleton--cell-sub" />
            </div>
            <div className="db-skeleton db-skeleton--cell-sm" />
            <div className="db-skeleton db-skeleton--cell-md" />
            <div className="db-skeleton db-skeleton--cell-sm" />
          </div>
        ))}
      </div>
    );
  }

  if (variant === "chart") {
    return (
      <div className="db-skeleton-chart">
        <div className="db-skeleton-chart-tabs">
          <div className="db-skeleton db-skeleton--tab" />
          <div className="db-skeleton db-skeleton--tab" />
        </div>
        <div className="db-skeleton-chart-area">
          <div className="db-skeleton-chart-yaxis">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="db-skeleton db-skeleton--y-label" />
            ))}
          </div>
          <div className="db-skeleton-chart-bars">
            {[60, 85, 45, 90, 55, 75, 65].map((h, i) => (
              <div
                key={i}
                className="db-skeleton db-skeleton--bar"
                style={{ height: `${h}%`, animationDelay: `${i * 0.06}s` }}
              />
            ))}
          </div>
        </div>
        <div className="db-skeleton-chart-xaxis">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="db-skeleton db-skeleton--x-label" />
          ))}
        </div>
        <div className="db-skeleton-chart-legend">
          <div className="db-skeleton db-skeleton--legend-item" />
          <div className="db-skeleton db-skeleton--legend-item" />
        </div>
      </div>
    );
  }

  if (variant === "wa-stats") {
    return (
      <div className="db-skeleton-wa-stats">
        <div className="db-skeleton-wa-grid">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="db-skeleton-wa-box">
              <div className="db-skeleton db-skeleton--wa-value" />
              <div className="db-skeleton db-skeleton--wa-label" />
            </div>
          ))}
        </div>
        <div className="db-skeleton-table">
          <div className="db-skeleton-table-header">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="db-skeleton db-skeleton--th" />
            ))}
          </div>
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="db-skeleton-table-row"
              style={{ animationDelay: `${i * 0.04}s` }}
            >
              <div className="db-skeleton db-skeleton--cell-md" />
              <div className="db-skeleton-table-cell db-skeleton-table-cell--wide">
                <div className="db-skeleton db-skeleton--cell-title" />
                <div className="db-skeleton db-skeleton--cell-sub" />
              </div>
              <div className="db-skeleton db-skeleton--cell-sm" />
              <div className="db-skeleton db-skeleton--cell-sm" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // default
  return (
    <div className="db-skeleton-default">
      <div className="db-skeleton db-skeleton--line-full" />
      <div className="db-skeleton db-skeleton--line-long" />
      <div className="db-skeleton db-skeleton--line-short" />
    </div>
  );
};

// ─── KPI card definitions ─────────────────────────────────────────────────────
const KPI_CARD_DEFS = [
  {
    key: "users",
    label: "Total Users",
    route: "/users/default",
    actionLabel: "View Users",
    color: "#0062FE",
    bg: "rgba(0,98,254,0.10)",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    key: "rooms",
    label: "Active Rooms",
    route: "/rooms/default",
    actionLabel: "View Rooms",
    color: "#8B5CF6",
    bg: "rgba(139,92,246,0.10)",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    key: "messages",
    label: "Total Messages",
    route: "/reports/default",
    actionLabel: "View Reports",
    color: "#0062FE",
    bg: "rgba(0,98,254,0.08)",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
  {
    key: "files",
    label: "Files Shared",
    route: "/media/default",
    actionLabel: "View Media",
    color: "#22C55E",
    bg: "rgba(34,197,94,0.10)",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    key: "whatsapp",
    label: "WhatsApp Routed",
    route: "/whatsapp/logs/default",
    actionLabel: "View Logs",
    color: "#22C55E",
    bg: "rgba(34,197,94,0.10)",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const buildKpiValues = (summary) => ({
  users: { value: summary.totalUsers?.toLocaleString() ?? "—", trend: null },
  rooms: { value: summary.activeRooms?.toLocaleString() ?? "—", trend: null },
  messages: {
    value: summary.totalMessages?.toLocaleString() ?? "—",
    trend: null,
  },
  files: { value: summary.totalFiles?.toLocaleString() ?? "—", trend: null },
  whatsapp: {
    value: summary.whatsapp?.routed?.toLocaleString() ?? "—",
    trend: null,
  },
});

const buildChartSeries = (chartData = []) => ({
  labels: chartData.map((d) =>
    new Date(d.date).toLocaleDateString("en-GB", { weekday: "short" }),
  ),
  messages: chartData.map((d) => d.messages),
  files: chartData.map((d) => d.files),
});

const ACTION_LABELS = {
  ROOM_CREATED: "created room",
  ROOM_MEMBER_ADDED: "added member to",
  ROOM_STATUS_CHANGED: "changed status of",
  ROOM_UPDATED: "updated room",
  WHATSAPP_MAPPING_CREATED: "created WhatsApp mapping",
};

const MODULE_META = {
  ROOM_CREATED: {
    label: "Rooms",
    color: "#8B5CF6",
    bg: "rgba(139,92,246,0.10)",
  },
  ROOM_MEMBER_ADDED: {
    label: "Rooms",
    color: "#8B5CF6",
    bg: "rgba(139,92,246,0.10)",
  },
  ROOM_STATUS_CHANGED: {
    label: "Rooms",
    color: "#F97316",
    bg: "rgba(249,115,22,0.10)",
  },
  ROOM_UPDATED: {
    label: "Rooms",
    color: "#8B5CF6",
    bg: "rgba(139,92,246,0.10)",
  },
  WHATSAPP_MAPPING_CREATED: {
    label: "WhatsApp",
    color: "#22C55E",
    bg: "rgba(34,197,94,0.10)",
  },
};

const initials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const AVATAR_COLORS = [
  "#0062FE",
  "#8B5CF6",
  "#22C55E",
  "#F97316",
  "#EF4444",
  "#6B7280",
];
const avatarColor = (id = "") =>
  AVATAR_COLORS[id.charCodeAt(0) % AVATAR_COLORS.length];

const relativeTime = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const roomStatusBadgeClass = (status) => {
  if (status === "ACTIVE") return "db-badge--active";
  if (status === "ARCHIVED") return "db-badge--archived";
  return "";
};

// ─── Line Chart (pure SVG) ────────────────────────────────────────────────────
const LineChart = ({ tab, labels, messages, files }) => {
  const W = 560,
    H = 200;
  const PAD = { top: 16, right: 16, bottom: 28, left: 44 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;

  const primary = tab === "messages" ? messages : files;
  const secondary = tab === "messages" ? files : messages;
  const maxVal = Math.max(...primary, ...secondary, 1) * 1.15;
  const count = labels.length || 1;

  const x = (i) => PAD.left + (i / Math.max(count - 1, 1)) * cW;
  const y = (v) => PAD.top + cH - (v / maxVal) * cH;

  const linePath = (d) =>
    d.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`).join(" ");
  const areaPath = (d) =>
    `${linePath(d)} L ${x(d.length - 1)} ${PAD.top + cH} L ${x(0)} ${PAD.top + cH} Z`;

  const gridVals = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(maxVal * f));
  const fmtVal = (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="db-line-chart"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id="gradMsg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0062FE" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#0062FE" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="gradFiles" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#22C55E" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#22C55E" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g className="chart-grid">
        {gridVals.map((v, i) => (
          <g key={i}>
            <line
              x1={PAD.left}
              y1={y(v)}
              x2={W - PAD.right}
              y2={y(v)}
              strokeDasharray="4 4"
            />
            <text
              x={PAD.left - 6}
              y={y(v) + 4}
              textAnchor="end"
              className="chart-label"
            >
              {fmtVal(v)}
            </text>
          </g>
        ))}
      </g>
      {secondary.length > 0 && (
        <path
          d={areaPath(secondary)}
          className="chart-area-files"
          strokeWidth="0"
        />
      )}
      {primary.length > 0 && (
        <path
          d={areaPath(primary)}
          className="chart-area-messages"
          strokeWidth="0"
        />
      )}
      {secondary.length > 0 && (
        <path
          d={linePath(secondary)}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          className="chart-line-files"
        />
      )}
      {primary.length > 0 && (
        <path
          d={linePath(primary)}
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
          className="chart-line-messages"
        />
      )}
      {primary.map((v, i) => (
        <circle
          key={i}
          cx={x(i)}
          cy={y(v)}
          r="3.5"
          fill="#0062FE"
          stroke="#fff"
          strokeWidth="2"
          className="chart-dot"
        />
      ))}
      {secondary.map((v, i) => (
        <circle
          key={i}
          cx={x(i)}
          cy={y(v)}
          r="3"
          fill="#22C55E"
          stroke="#fff"
          strokeWidth="2"
          className="chart-dot"
        />
      ))}
      {labels.map((d, i) => (
        <text
          key={i}
          x={x(i)}
          y={H - 6}
          textAnchor="middle"
          className="chart-label"
        >
          {d}
        </text>
      ))}
    </svg>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const [chartTab, setChartTab] = useState("messages");
  const [dateRange, setDateRange] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState({
    labels: [],
    messages: [],
    files: [],
  });
  const [topRooms, setTopRooms] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [whatsappLogs, setWhatsappLogs] = useState([]);
  const [kpiValues, setKpiValues] = useState({});

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDashboardSummaryApi();
      const data = res.data;
      setSummary(data.summary);
      setKpiValues(buildKpiValues(data.summary));
      setChartData(buildChartSeries(data.chartData));
      setTopRooms(data.topRooms ?? []);
      setRecentActivities(data.recentActivities ?? []);
      setWhatsappLogs(data.whatsappLogs ?? []);
    } catch (err) {
      setError("Failed to load dashboard data. Please try again.");
      console.error("Dashboard API error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [dateRange]);

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const maxRoomMembers = Math.max(...topRooms.map((r) => r.members), 1);

  return (
    <div className="dashboard">
      {/* ── Page Header ── */}
      <div className="db-page-header">
        <div className="db-page-header__left">
          <span className="db-page-header__label">Overview</span>
          <h1 className="db-page-header__title">Dashboard</h1>
          <p className="db-page-header__subtitle">
            {dateStr} · Murasal One Admin Portal
          </p>
        </div>
        <div className="db-page-header__right">
          <select
            className="db-date-select"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="1d">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button
            className="db-btn db-btn--secondary"
            onClick={fetchDashboard}
            disabled={loading}
          >
            <RefreshIcon /> {loading ? "Loading…" : "Refresh"}
          </button>
          
        </div>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div className="db-error-banner">
          <span>{error}</span>
          <button onClick={fetchDashboard}>Retry</button>
        </div>
      )}

      {/* ── KPI Cards ── */}
      <div className="db-kpi-grid">
        {KPI_CARD_DEFS.map((card) => {
          const kpi = kpiValues[card.key] ?? {};
          return (
            <div
              key={card.key}
              className="db-kpi-card"
              style={{ "--kpi-color": card.color, "--kpi-bg": card.bg }}
              onClick={() => !loading && navigate(card.route)}
            >
              <div className="db-kpi-card__top">
                <div className="db-kpi-card__icon">{card.icon}</div>
                {!loading && kpi.trend && (
                  <span
                    className={`db-kpi-card__trend db-kpi-card__trend--${kpi.trendDir}`}
                  >
                    {kpi.trendDir === "up" ? <ArrowUp /> : <ArrowDown />}{" "}
                    {kpi.trend}
                  </span>
                )}
              </div>

              {loading ? (
                <CardLoader variant="kpi" />
              ) : (
                <>
                  <div className="db-kpi-card__value">{kpi.value ?? "—"}</div>
                  <div className="db-kpi-card__label">{card.label}</div>
                  <div className="db-kpi-card__action">
                    {card.actionLabel} <ArrowRight />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Activity Chart + Recent Activity ── */}
      <div className="db-mid-grid">
        {/* Chart card */}
        <div className="db-card" style={{ animationDelay: "0.15s" }}>
          <div className="db-card__header">
            <div>
              <h3 className="db-card__title">Activity Trend</h3>
              <p className="db-card__subtitle">Messages & files over time</p>
            </div>
            <button
              className="db-view-all"
              onClick={() => navigate("/reports/default")}
            >
              Full Report <ArrowRight />
            </button>
          </div>
          <div className="db-card__body db-chart-wrap">
            {loading ? (
              <CardLoader variant="chart" />
            ) : (
              <>
                <div className="db-chart-tabs">
                  <button
                    className={chartTab === "messages" ? "active" : ""}
                    onClick={() => setChartTab("messages")}
                  >
                    Messages
                  </button>
                  <button
                    className={chartTab === "files" ? "active" : ""}
                    onClick={() => setChartTab("files")}
                  >
                    Files
                  </button>
                </div>
                <LineChart
                  tab={chartTab}
                  labels={chartData.labels}
                  messages={chartData.messages}
                  files={chartData.files}
                />
                <div className="db-chart-legend">
                  <div className="db-chart-legend__item">
                    <div
                      className="db-chart-legend__dot"
                      style={{ background: "#0062FE" }}
                    />{" "}
                    Messages
                  </div>
                  <div className="db-chart-legend__item">
                    <div
                      className="db-chart-legend__dot"
                      style={{ background: "#22C55E" }}
                    />{" "}
                    Files Shared
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Recent Activity card */}
        <div className="db-card" style={{ animationDelay: "0.2s" }}>
          <div className="db-card__header">
            <div>
              <h3 className="db-card__title">Recent Activity</h3>
              <p className="db-card__subtitle">Latest admin & user events</p>
            </div>
            <button className="db-view-all" onClick={() => navigate("/audit/default")}>
              All Logs <ArrowRight />
            </button>
          </div>
          <div className="db-card__body db-scroll" style={{ maxHeight: 310 }}>
            {loading ? (
              <CardLoader variant="activity" />
            ) : recentActivities.length === 0 ? (
              <p className="db-empty">No recent activity.</p>
            ) : (
              <div className="db-activity__list">
                {recentActivities.map((a) => {
                  const actorName = a.actor?.fullName ?? "System";
                  const actionLabel = ACTION_LABELS[a.action] ?? a.action;
                  const target =
                    a.details?.name ??
                    a.details?.status ??
                    a.entityId?.slice(0, 8);
                  const mod = MODULE_META[a.action] ?? {
                    label: "System",
                    color: "#6B7280",
                    bg: "#F3F4F6",
                  };
                  const color = avatarColor(a.actorUserId ?? "");
                  return (
                    <div key={a.id} className="db-activity__item">
                      <div
                        className="db-activity__avatar"
                        style={{ background: color }}
                      >
                        {initials(actorName)}
                      </div>
                      <div className="db-activity__content">
                        <div className="db-activity__text">
                          <strong>{actorName}</strong> {actionLabel}{" "}
                          {target && <strong>{target}</strong>}
                        </div>
                        <div className="db-activity__meta">
                          <span className="db-activity__time">
                            {relativeTime(a.createdAt)}
                          </span>
                          <span
                            className="db-activity__module"
                            style={{ color: mod.color, background: mod.bg }}
                          >
                            {mod.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Bottom: Top Rooms + WhatsApp Routing ── */}
      <div className="db-bottom-grid">
        {/* Top Rooms */}
        <div className="db-card" style={{ animationDelay: "0.25s" , height : "400px" , overflowY : "scroll" }}>
          <div className="db-card__header">
            <div>
              <h3 className="db-card__title">Top Active Rooms</h3>
              <p className="db-card__subtitle">Sorted by member count</p>
            </div>
            <button className="db-view-all" onClick={() => navigate("/rooms/default")}>
              All Rooms <ArrowRight />
            </button>
          </div>
          <div className="db-card__body">
            {loading ? (
              <CardLoader variant="table" />
            ) : topRooms.length === 0 ? (
              <p className="db-empty">No rooms found.</p>
            ) : (
              <table className="db-table">
                <thead>
                  <tr>
                    <th>Room</th>
                    <th>Type</th>
                    <th>Members</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {topRooms.map((r) => (
                    <tr key={r.id} onClick={() => navigate("/rooms/default")}>
                      <td>
                        <div className="db-table__name">{r.roomName}</div>
                        <div className="db-table__sub">{r.id.slice(0, 8)}…</div>
                      </td>
                      <td>
                        <span
                          className="db-badge"
                          style={{
                            color: "#6B7280",
                            background: "#F3F4F6",
                            fontSize: 11,
                            padding: "2px 7px",
                          }}
                        >
                          {r.roomType}
                        </span>
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <div className="db-table__bar-wrap">
                            <div
                              className="db-table__bar"
                              style={{
                                width: `${(r.members / maxRoomMembers) * 100}%`,
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: 12,
                              color: "var(--text-muted)",
                              minWidth: 20,
                              textAlign: "right",
                            }}
                          >
                            {r.members}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`db-badge ${roomStatusBadgeClass(r.status)}`}
                        >
                          {r.status.toLowerCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* WhatsApp Routing */}
        <div className="db-card" style={{ animationDelay: "0.3s", height: "400px" , overflowY: "scroll" }}>
          <div className="db-card__header">
            <div>
              <h3 className="db-card__title">WhatsApp Routing</h3>
              <p className="db-card__subtitle">Recent inbound routing events</p>
            </div>
            <button
              className="db-view-all"
              onClick={() => navigate("/whatsapp/logs/default")}
            >
              View Logs <ArrowRight />
            </button>
          </div>
          <div className="db-card__body">
            {loading ? (
              <CardLoader variant="wa-stats" />
            ) : (
              <>
                {summary && (
                  <div className="db-wa-stats">
                    <div className="db-wa-stats__box">
                      <div
                        className="db-wa-stats__box-value"
                        style={{ color: "#0062FE" }}
                      >
                        {summary.whatsapp?.routed ?? 0}
                      </div>
                      <div className="db-wa-stats__box-label">Routed</div>
                    </div>
                    <div className="db-wa-stats__box">
                      <div
                        className="db-wa-stats__box-value"
                        style={{ color: "#EF4444" }}
                      >
                        {summary.whatsapp?.failed ?? 0}
                      </div>
                      <div className="db-wa-stats__box-label">Failed</div>
                    </div>
                    <div className="db-wa-stats__box">
                      <div
                        className="db-wa-stats__box-value"
                        style={{ color: "#6B7280" }}
                      >
                        {summary.whatsapp?.rejected ?? 0}
                      </div>
                      <div className="db-wa-stats__box-label">Rejected</div>
                    </div>
                  </div>
                )}
                {whatsappLogs.length === 0 ? (
                  <p className="db-empty">No WhatsApp routing logs.</p>
                ) : (
                  <table className="db-table">
                    <thead>
                      <tr>
                        <th>Identity</th>
                        <th>Action</th>
                        <th>Status</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {whatsappLogs.map((w) => (
                        <tr
                          key={w.id}
                          onClick={() => navigate("/whatsapp/logs/default")}
                        >
                          <td style={{ fontFamily: "monospace", fontSize: 12 }}>
                            {w.whatsappIdentity ?? "—"}
                          </td>
                          <td>
                            <div
                              className="db-table__name"
                              style={{ fontSize: 12 }}
                            >
                              {ACTION_LABELS[w.action] ?? w.action}
                            </div>
                            <div className="db-table__sub">
                              {w.details?.mappingType ?? ""}
                            </div>
                          </td>
                          <td>
                            <span
                              className={`db-badge db-badge--${w.status?.toLowerCase()}`}
                            >
                              {w.status?.toLowerCase()}
                            </span>
                          </td>
                          <td
                            style={{
                              fontSize: 11,
                              color: "var(--text-muted)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {relativeTime(w.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
